import { Lock } from 'lucide-react';

// The auth shell stays in `features/auth/status/` - it is what all five
// non-form auth screens share, and account recovery only borrows it.
import {
  AuthStatusButton,
  AuthStatusCard,
  AuthStatusError,
  AuthStatusLink,
} from '@/components/features/auth/status';

export interface DeactivatedAccountViewProps {
  email: string;
  /** The recovery-link request is in flight. */
  isPending: boolean;
  /** It came back clean. The button stays, with its label swapped. */
  isDone: boolean;
  /** Resolved copy, never a raw rejection - see `lib/account/restore-error`. */
  error: string | null;
  onRequest: () => void;
  onUseDifferentAccount: () => void;
}

/**
 * The soft-deleted-account screen, with no idea how the request is made.
 *
 * Split out of `DeactivatedAccountCard` so its four states are props rather
 * than `useOneShotAction` internals. Three of them - pending, done, failed -
 * previously had no way to exist in the catalog at all, so they lived as page
 * journeys on `App/Auth/Desktop/Behaviour` and the component tier showed a
 * single resting frame. The state itself has not moved anywhere synthetic: the
 * container one level up still owns the real hook.
 *
 * Note the button is kept in the done state rather than unmounted, with only
 * its label swapped. This screen used to unmount it while `AuthSuccess` - the
 * other consumer of the same hook - kept its own and swapped the label. Two
 * copies of one interaction, drifted.
 */
export function DeactivatedAccountView({
  email,
  isPending,
  isDone,
  error,
  onRequest,
  onUseDifferentAccount,
}: DeactivatedAccountViewProps) {
  return (
    <AuthStatusCard
      tone='warning'
      icon={<Lock className='h-5 w-5' />}
      title='This account is deactivated'
      body={
        isDone
          ? // Hedged on purpose: `request-restore` always answers `{ sent: true }`
            // to avoid account enumeration, so the UI genuinely does not know
            // whether an email went out.
            `If ${email} matches a recently deactivated account, we have sent a recovery link. It expires in 30 minutes.`
          : 'You deleted this account. Everything is still here for 30 days from the deletion date, and a recovery link will bring it back.'
      }
    >
      <AuthStatusButton
        pending={isPending}
        disabled={isDone}
        onClick={onRequest}
      >
        {isDone ? 'Recovery link sent' : 'Email me a recovery link'}
      </AuthStatusButton>
      <AuthStatusLink onClick={onUseDifferentAccount}>
        Use a different account
      </AuthStatusLink>
      {error && <AuthStatusError message={error} />}
    </AuthStatusCard>
  );
}
